import { useCallback, useEffect, useState, useMemo } from 'react'
import { taskService } from '@/services'
import { useAuth } from '@/hooks'
import type { Task } from '@/types'
import { tasksListConfig } from '@/lib/config/node-list-configs'
import { NodeListPage } from '@/components/nodes/NodeListPage'
import type { TasksFiltersState } from '@/components/tasks'
import type { TaskGroupByOption } from '@/lib/config/node-grouping-configs'

export default function TasksPage() {
  const { user } = useAuth()
  const [tasks, setTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState('my-assigned')
  const [showFilters, setShowFilters] = useState(false)
  const [groupBy, setGroupBy] = useState<TaskGroupByOption>('none')

  // Initialize filters with default values
  const [filters, setFilters] = useState<TasksFiltersState>(tasksListConfig.defaultFilters)

  // Handler for partial filter updates
  const handleFiltersChange = (partialFilters: Partial<TasksFiltersState>) => {
    setFilters((prev: TasksFiltersState) => ({ ...prev, ...partialFilters }) as TasksFiltersState)
  }

  // Get unique users from tasks for assignee filter
  const uniqueUsers = useMemo(() => {
    if (!tasks.length) return []
    return Array.from(
      new Map(
        tasks.filter((task) => task.assignee).map((task) => [task.assignee!.id, task.assignee!])
      ).values()
    )
  }, [tasks])

  const loadTasks = useCallback(async () => {
    if (!user) return

    try {
      setLoading(true)
      const allTasks = await taskService.getAll()

      // SuperAdmins see all tasks, others see only their own
      if (user.role === 'SuperAdmin') {
        setTasks(allTasks)
      } else {
        // Filter tasks for the user (assigned or created by them)
        const userTasks = allTasks.filter(
          (task) => task.assignee?.id === user.id || task.creator.id === user.id
        )
        setTasks(userTasks)
      }
    } catch (error) {
      console.error('Failed to load tasks:', error)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    loadTasks()
  }, [loadTasks])

  return (
    <NodeListPage
      nodes={tasks}
      loading={loading}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      showFilters={showFilters}
      onFiltersToggle={() => setShowFilters(!showFilters)}
      filters={filters}
      onFiltersChange={handleFiltersChange}
      groupBy={groupBy}
      onGroupByChange={setGroupBy}
      config={
        {
          ...tasksListConfig,
          filterNodes: (
            nodes: Task[],
            tab: string,
            filterState: TasksFiltersState,
            query: string
          ) =>
            tasksListConfig.filterNodes(
              nodes,
              tab,
              filterState,
              query,
              user ? { id: user.id, role: user.role } : undefined
            ),
          filterComponentProps: {
            ...tasksListConfig.filterComponentProps,
            availableUsers: uniqueUsers,
          },
        } as typeof tasksListConfig
      }
    />
  )
}
